/**
 * Katalog VoltValue w projekcie dachu 3D: dane modeli z API zamienione na kształty projektu
 * (kroki Moduły, Falownik, Magazyn). Bez zapytań i bez Reacta, żeby dało się to przetestować
 * na nagranych odpowiedziach. Zapytania: `api/katalog3d.js`, wybór: `components/projekt3d/WyborModelu.jsx`.
 *
 * ═══ KSZTAŁTY ═══
 * moduły:   { kategoria: 'moduly', id, producent, seria, nazwa, moc (W), dlugosc, szerokosc, grubosc (m),
 *             typ: 'fullblack' | 'standard' | 'bifacial', rzedyOgniw, bifacjalnosc, img, kartaUrl, zrodlo, brakujace }
 * falownik: { kategoria: 'falownik', id, producent, seria, nazwa, mocKw, fazy (1 | 3 | null), hybrydowy,
 *             img, kartaUrl, zrodlo, brakujace }
 * magazyn:  { kategoria: 'magazyn', id, producent, seria, nazwa, pojemnoscKwh, uzytkowaKwh, mocKw, sprawnoscProc,
 *             img, kartaUrl, zrodlo, brakujace }
 * `zrodlo`: 'katalog' albo 'reczny'. `brakujace`: pola, których katalog nie podał (null w obiekcie).
 *
 * ═══ ZASADA: BRAK DANYCH TO NULL, NIE ZGADYWANIE ═══
 * Ponad połowa modułów w katalogu nie ma wymiarów (2026-10: wymiary ma 262 z 582). Takiego pola nie
 * uzupełniamy typową wartością, tylko zostawiamy null i wpisujemy do `brakujace`, a wybór
 * prosi użytkownika o wartość z karty katalogowej. Wyjątki to rachunki na danych z katalogu:
 * energia użytkowa magazynu z pojemności i DoD (tak definiują ją karty) oraz liczba rzędów
 * ogniw z liczby ogniw (tylko do wyglądu tekstury).
 */

import { PRESETY_MODULOW } from './projekt'

export const BIFACJALNOSC_DOMYSLNA = 0.8

/** Pola, bez których projekt nie ułoży modułów albo nie policzy mocy. */
export const WYMAGANE = {
  moduly: ['moc', 'dlugosc', 'szerokosc'],
  falownik: ['mocKw'],
  magazyn: ['pojemnoscKwh'],
}

/** Nazwy typów modułu w projekcie (jak `TYPY_MODULOW` w projekt.js). */
export const NAZWY_TYPOW = { fullblack: 'Full black', standard: 'Standard', bifacial: 'Bifacial' }

// Wartości startowe wpisywania ręcznego, gdy nie ma z czego ich wziąć.
const FALOWNIK_RECZNY = { mocKw: 10, fazy: 3, hybrydowy: false }
const MAGAZYN_RECZNY = { pojemnoscKwh: 10, sprawnoscProc: 95 }

/* ═══ Drobne narzędzia ═══════════════════════════════════════════════════════════════════════ */

const liczba = (x) => {
  if (x == null || x === '' || typeof x === 'boolean') return null
  const n = typeof x === 'number' ? x : Number(String(x).trim().replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
const dodatnia = (x) => {
  const n = liczba(x)
  return n != null && n > 0 ? n : null
}
const tekst = (x) => {
  if (x == null) return null
  const t = String(x).replace(/\s+/g, ' ').trim()
  return t || null
}
const pierwsza = (...wartosci) => {
  const w = wartosci.find((x) => x != null && x !== '')
  return w === undefined ? null : w
}
const zaokr = (x, miejsc = 2) => (x == null ? null : Math.round(x * 10 ** miejsc) / 10 ** miejsc)
const mmNaM = (mm) => {
  const n = dodatnia(mm)
  return n == null ? null : zaokr(n / 1000, 4)
}

/** Adres zdjęcia albo karty: tylko http(s), bo trafia do `src` i `href`. */
const adres = (url) => {
  const t = tekst(url)
  return t && /^(https?:)?\/\//i.test(t) ? t : null
}

/** Kopia obiektu bez pól null i undefined (np. do scalenia z presetem projektu). */
export const bezBrakow = (obiekt) => Object.fromEntries(
  Object.entries(obiekt || {}).filter(([, v]) => v != null),
)

/** Liczba po polsku: 2,56; bez zbędnych zer. */
export const liczbaPl = (x, maksMiejsc = 2) => (x == null || !Number.isFinite(x) ? '' : x.toLocaleString('pl-PL', {
  maximumFractionDigits: maksMiejsc,
}))

const JEDNOSTKI = { moduly: 'W', falownik: 'kW', magazyn: 'kWh' }

/** „450 W”, „12 kW”, „2,56 kWh”: moc modułu i falownika, pojemność magazynu. */
export const formatujMoc = (kategoria, x) => (x == null ? '' : `${liczbaPl(x, kategoria === 'moduly' ? 0 : 2)} ${JEDNOSTKI[kategoria]}`)

/** „1 oferta”, „3 oferty”, „5 ofert”, „22 oferty”. */
export const liczbaOfert = (n) => {
  if (n === 1) return '1 oferta'
  const j = n % 10
  const d = n % 100
  return j >= 2 && j <= 4 && (d < 12 || d > 14) ? `${n} oferty` : `${n} ofert`
}

const fazyTekst = (f) => (f === 1 ? '1 faza' : `${f} fazy`)

/** Fazy falownika z liczby albo opisu z katalogu („3 Fazowy”, „1 Fazowy”). */
export const fazyZOpisu = (x) => {
  if (x === 1 || x === 3) return x
  const t = (tekst(x) || '').toLowerCase()
  if (t.startsWith('3') || t.startsWith('trój') || t.startsWith('troj')) return 3
  if (t.startsWith('1') || t.startsWith('jedno')) return 1
  return null
}

/* ═══ Moduły ═════════════════════════════════════════════════════════════════════════════════ */

/**
 * Typ modułu w projekcie z typów katalogu i koloru ramy. Bifacial wygrywa (szkło-szkło),
 * potem Full Black albo czarna rama, reszta to standard.
 */
export const typModulu = (typy = [], rama = null) => {
  const lista = (typy || []).map((t) => String(t))
  if (lista.some((t) => /bifacial|bifacjal/i.test(t))) return 'bifacial'
  if (lista.some((t) => /full\s*black/i.test(t)) || /czarn/i.test(rama || '')) return 'fullblack'
  return 'standard'
}

const typyZDanych = (d) => [
  ...(Array.isArray(d.types) ? d.types : []),
  ...(Array.isArray(d.typy) ? d.typy : []),
  d.type, d.type_secondary, d.rodzaj,
].map(tekst).filter(Boolean).filter((t, i, a) => a.indexOf(t) === i)

// Moduł z półogniwami ma 6 kolumn ogniw wzdłuż krótszego boku (tak rysuje tekstura w Panele.jsx).
const KOLUMNY_OGNIW = 6
const RZEDY = [14, 28]
const ROZSTAW_RZEDU_M = [0.07, 0.125]

/**
 * Rzędy półogniw wzdłuż dłuższego boku z `cells_series` karty, albo null.
 *
 * Źródła liczą ogniwa na dwa sposoby: CEC i PVsyst podają ogniwa SZEREGOWE (54 przy 108
 * półogniwach), karty producentów i zestawienie PVIndex WSZYSTKIE półogniwa (108). Mamy więc
 * dwóch kandydatów, n / 3 i n / 6. Zostaje ten, który daje realną liczbę rzędów (14 do 28),
 * a przy znanej długości także realny rozstaw rzędu (7 do 12,5 cm). Gdy zostaje dwóch albo
 * żaden, zwracamy null: tekstura użyje wtedy swojej domyślnej liczby.
 * Wynik służy tylko wyglądowi. Ogniwa cięte na trzy (Trina Vertex S+) z CEC wyjdą za rzadko.
 */
export const rzedyZOgniw = (liczbaOgniw, dlugoscM = null) => {
  const n = liczba(liczbaOgniw)
  if (n == null || !Number.isInteger(n) || n <= 0) return null
  const kandydaci = [n / (KOLUMNY_OGNIW / 2), n / KOLUMNY_OGNIW]
    .filter((r) => Number.isInteger(r) && r >= RZEDY[0] && r <= RZEDY[1])
  const zgodne = dlugoscM > 0
    ? kandydaci.filter((r) => dlugoscM / r >= ROZSTAW_RZEDU_M[0] && dlugoscM / r <= ROZSTAW_RZEDU_M[1])
    : kandydaci
  return zgodne.length === 1 ? zgodne[0] : null
}

const brakujacePola = (obiekt, pola) => pola.filter((k) => obiekt[k] == null)

/**
 * Moduł z katalogu. Przyjmuje pełne dane modelu (`GET get_panel_model/{id}`), a gdy ich nie ma,
 * także wiersz listy (`panel_models/`) albo wynik wyszukiwarki (`szukaj_modeli/`): wtedy
 * wymiary trafiają do `brakujace`.
 */
export const modulZKatalogu = (d = {}) => {
  let dlugosc = mmNaM(d.module_length)
  let szerokosc = mmNaM(d.module_width)
  // Projekt zakłada, że długość to dłuższy bok.
  if (dlugosc != null && szerokosc != null && szerokosc > dlugosc) [dlugosc, szerokosc] = [szerokosc, dlugosc]
  const typy = typyZDanych(d)
  const rama = tekst(d.frame_colour)
  const typ = typModulu(typy, rama)
  const spec = d.electrical_spec?.values || {}
  const m = {
    kategoria: 'moduly',
    id: pierwsza(d.id, d.model_id),
    producent: tekst(pierwsza(d.manufacturer, d.producent)),
    seria: tekst(pierwsza(d.series, d.seria)),
    nazwa: tekst(pierwsza(d.name, d.nazwa)),
    moc: dodatnia(pierwsza(d.power, d.moc_w, d.moc)),
    dlugosc,
    szerokosc,
    grubosc: mmNaM(d.module_thickness),
    typ,
    rzedyOgniw: rzedyZOgniw(spec.cells_series, dlugosc),
    bifacjalnosc: typ === 'bifacial' ? BIFACJALNOSC_DOMYSLNA : 0,
    img: adres(d.img_url),
    kartaUrl: adres(pierwsza(d.electrical_spec?.datasheet_url, d.karta_url)),
    zrodlo: 'katalog',
  }
  m.brakujace = [
    ...brakujacePola(m, ['moc', 'dlugosc', 'szerokosc', 'grubosc']),
    ...(typy.length || rama ? [] : ['typ']),
  ]
  return m
}

/* ═══ Falownik i magazyn ═════════════════════════════════════════════════════════════════════ */

/**
 * Falownik z katalogu: `GET sprawdz_oferte/model?kategoria=falownik` (moc_kw, fazy, typ),
 * wiersz listy `inverter_models/` (power, phase, type) albo wynik wyszukiwarki (moc, rodzaj, bez faz).
 */
export const falownikZKatalogu = (d = {}) => {
  const typ = tekst(pierwsza(d.typ, d.type, d.rodzaj))
  const f = {
    kategoria: 'falownik',
    id: pierwsza(d.model_id, d.id),
    producent: tekst(pierwsza(d.producent, d.manufacturer)),
    seria: tekst(pierwsza(d.seria, d.series)),
    nazwa: tekst(pierwsza(d.nazwa, d.name)),
    mocKw: dodatnia(pierwsza(d.moc_kw, d.power, d.moc)),
    fazy: fazyZOpisu(pierwsza(d.fazy, d.phase)),
    hybrydowy: typ ? /hybryd/i.test(typ) : false,
    img: adres(d.img_url),
    kartaUrl: adres(d.karta_url),
    zrodlo: 'katalog',
  }
  f.brakujace = [...brakujacePola(f, ['mocKw', 'fazy']), ...(typ ? [] : ['hybrydowy'])]
  return f
}

/**
 * Magazyn z katalogu: `GET sprawdz_oferte/model?kategoria=magazyn` (pojemność, moc rozładowania,
 * `karta_magazynu`) albo wiersz listy `magazine_models/` (sama pojemność).
 *
 * Moc to wyłącznie moc rozładowania z karty. Iloczyn napięcia i prądu (`moc_maks_z_pradu_kw`)
 * backend podaje osobno jako maksimum do wglądu, bo zawyża moc, więc go tu nie bierzemy.
 * Energia użytkowa: z karty, a bez niej pojemność × DoD z tej samej karty.
 */
export const magazynZKatalogu = (d = {}) => {
  const karta = d.karta_magazynu || {}
  const pojemnoscKwh = dodatnia(pierwsza(d.pojemnosc_kwh, d.capacity))
  const dod = dodatnia(karta.dod_procent)
  const uzytkowaKwh = dodatnia(karta.energia_uzytkowa_kwh)
    ?? (pojemnoscKwh != null && dod != null && dod <= 100 ? zaokr((pojemnoscKwh * dod) / 100, 2) : null)
  const m = {
    kategoria: 'magazyn',
    id: pierwsza(d.model_id, d.id),
    producent: tekst(pierwsza(d.producent, d.manufacturer)),
    seria: tekst(pierwsza(d.seria, d.series)),
    nazwa: tekst(pierwsza(d.nazwa, d.name)),
    pojemnoscKwh,
    uzytkowaKwh,
    mocKw: dodatnia(d.moc_rozladowania_kw),
    sprawnoscProc: dodatnia(karta.sprawnosc_procent),
    img: adres(d.img_url),
    kartaUrl: adres(d.karta_url),
    zrodlo: 'katalog',
  }
  m.brakujace = brakujacePola(m, ['pojemnoscKwh', 'uzytkowaKwh', 'mocKw', 'sprawnoscProc'])
  return m
}

const MAPOWANIE = { moduly: modulZKatalogu, falownik: falownikZKatalogu, magazyn: magazynZKatalogu }

/** Obiekt projektu z danych katalogu dla kategorii 'moduly' | 'falownik' | 'magazyn'. */
export const wyborZKatalogu = (kategoria, dane) => {
  const f = MAPOWANIE[kategoria]
  if (!f) throw new Error(`Nieznana kategoria: ${kategoria}`)
  return f(dane || {})
}

/* ═══ Wpisywanie ręczne i poprawki ═══════════════════════════════════════════════════════════ */

/**
 * Obiekt wpisany ręcznie. Liczby startowe z `baza` (np. obecnie wybrany model), a czego tam
 * nie ma, z domyślnego presetu projektu (moduły) albo typowych wartości (falownik, magazyn).
 */
export const wyborReczny = (kategoria, baza = null) => {
  const b = bezBrakow(baza)
  const wspolne = { id: null, producent: null, seria: null, nazwa: null, img: null, kartaUrl: null, zrodlo: 'reczny', brakujace: [] }
  if (kategoria === 'moduly') {
    const p = PRESETY_MODULOW[0]
    const typ = b.typ || p.typ
    return {
      kategoria,
      ...wspolne,
      moc: b.moc ?? p.moc,
      dlugosc: b.dlugosc ?? p.dlugosc,
      szerokosc: b.szerokosc ?? p.szerokosc,
      grubosc: b.grubosc ?? p.grubosc,
      typ,
      rzedyOgniw: b.rzedyOgniw ?? p.rzedyOgniw ?? null,
      bifacjalnosc: typ === 'bifacial' ? (b.bifacjalnosc || BIFACJALNOSC_DOMYSLNA) : 0,
    }
  }
  if (kategoria === 'falownik') {
    return {
      kategoria,
      ...wspolne,
      mocKw: b.mocKw ?? FALOWNIK_RECZNY.mocKw,
      fazy: b.fazy ?? FALOWNIK_RECZNY.fazy,
      hybrydowy: b.hybrydowy ?? FALOWNIK_RECZNY.hybrydowy,
    }
  }
  if (kategoria === 'magazyn') {
    return {
      kategoria,
      ...wspolne,
      pojemnoscKwh: b.pojemnoscKwh ?? MAGAZYN_RECZNY.pojemnoscKwh,
      uzytkowaKwh: b.uzytkowaKwh ?? null,
      mocKw: b.mocKw ?? null,
      sprawnoscProc: b.sprawnoscProc ?? MAGAZYN_RECZNY.sprawnoscProc,
    }
  }
  throw new Error(`Nieznana kategoria: ${kategoria}`)
}

/**
 * Poprawka użytkownika. Pole z wartością znika z `brakujace`. Zmiana typu modułu ustawia
 * bifacjalność (0,8 dla bifacial, 0 dla reszty), a zmiana pojemności magazynu przelicza
 * energię użytkową w tej samej proporcji (DoD się nie zmienia).
 */
export const poprawWybor = (obiekt, zmiany = {}) => {
  if (!obiekt) return obiekt
  const nowy = { ...obiekt, ...zmiany }
  if (obiekt.kategoria === 'moduly' && 'typ' in zmiany && !('bifacjalnosc' in zmiany)) {
    nowy.bifacjalnosc = zmiany.typ === 'bifacial' ? (obiekt.bifacjalnosc || BIFACJALNOSC_DOMYSLNA) : 0
  }
  if (obiekt.kategoria === 'magazyn' && 'pojemnoscKwh' in zmiany && !('uzytkowaKwh' in zmiany)) {
    const stara = obiekt.pojemnoscKwh
    const nowa = zmiany.pojemnoscKwh
    if (obiekt.uzytkowaKwh != null && stara > 0 && nowa > 0) nowy.uzytkowaKwh = zaokr((obiekt.uzytkowaKwh * nowa) / stara, 2)
  }
  nowy.brakujace = (obiekt.brakujace || []).filter((k) => !(k in zmiany) || zmiany[k] == null)
  return nowy
}

/** Czy obiekt ma wszystko, czego projekt potrzebuje (`WYMAGANE`). */
export const kompletny = (obiekt) => !!obiekt && (WYMAGANE[obiekt.kategoria] || []).every((k) => obiekt[k] != null)

const NAZWY_RECZNE = { moduly: 'Własny moduł', falownik: 'Własny falownik', magazyn: 'Własny magazyn' }

/** Jedna linijka do zwiniętego kroku: „Jinko Solar Tiger Neo, 450 W”, „Własny falownik, 10 kW, 3 fazy”. */
export const opisWyboru = (o) => {
  if (!o) return null
  const nazwa = o.zrodlo === 'reczny'
    ? NAZWY_RECZNE[o.kategoria]
    : ([o.producent, o.seria].filter(Boolean).join(' ') || krotkaNazwa(o.nazwa, o.producent))
  const dane = {
    moduly: [o.moc != null && formatujMoc('moduly', o.moc)],
    falownik: [o.mocKw != null && formatujMoc('falownik', o.mocKw), o.fazy && fazyTekst(o.fazy), o.hybrydowy && 'hybrydowy'],
    magazyn: [o.pojemnoscKwh != null && formatujMoc('magazyn', o.pojemnoscKwh)],
  }[o.kategoria] || []
  return [nazwa, ...dane].filter(Boolean).join(', ')
}

/* ═══ Listy w wyborze ════════════════════════════════════════════════════════════════════════ */

// Znaki między członami nazwy w sklepach: odstęp, przecinek, dwukropek, ukośnik, kreska i półpauza
// (U+2013, zapisana kodem). Kreska zostaje na końcu klasy znaków, żeby nie tworzyła zakresu.
const SEPARATOR = `\\s,:;/|${String.fromCharCode(0x2013)}-`
// Słowa, od których sklepy zaczynają nazwy. Zdejmujemy je tylko z początku nazwy.
const OGOLNE = new RegExp(`^(?:modu[łl]y?|panele?|fotowoltaiczn[a-z]*|pv|inwerter[a-z]*|falownik[a-z]*|mikroinwerter[a-z]*|hybrydow[a-z]*|sieciow[a-z]*|tr[óo]jfazow[a-z]*|jednofazow[a-z]*|magazyn[a-z]*|energii|bater[a-z]*|akumulator[a-z]*|zestaw[a-z]*)(?=$|[(${SEPARATOR}])[${SEPARATOR}]*`, 'i')
const ZNAK_SLOWA = 'A-Za-z0-9\\u00C0-\\u024F'
const SEPARATORY = new RegExp(`^[${SEPARATOR}]+|[${SEPARATOR}]+$`, 'g')
const escapuj = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const bezProducenta = (t, producent) => {
  const slowa = (producent || '').trim().split(/\s+/).filter(Boolean).map(escapuj)
  if (!slowa.length) return t
  const warianty = [slowa.join('\\s*')]
  // „Jinko”, „LONGi”, „Trina” bez drugiego członu; dwuliterowe („JA”) zostają, łatwo o pomyłkę.
  if (slowa.length > 1 && slowa[0].length >= 4) warianty.push(slowa[0])
  return warianty.reduce((wynik, w) => wynik.replace(
    new RegExp(`(^|[^${ZNAK_SLOWA}])(?:${w})(?=$|[^${ZNAK_SLOWA}])`, 'gi'), '$1',
  ), t)
}

/**
 * Nazwa modelu bez ogólnych słów sklepu i bez producenta, do list w wąskim panelu:
 * „Moduł Fotowoltaiczny Jinko Solar Tiger Neo JKM450…” -> „Tiger Neo JKM450…”.
 * Gdy z nazwy prawie nic nie zostaje, zwraca pełną nazwę.
 */
export const krotkaNazwa = (nazwa, producent = null) => {
  const pelna = tekst(nazwa)
  if (!pelna) return ''
  let t = pelna
  for (let i = 0; i < 4; i++) {
    const przed = t
    while (OGOLNE.test(t)) t = t.replace(OGOLNE, '')
    t = bezProducenta(t, producent)
      .replace(/\(\s*\)/g, ' ')
      .replace(/\s+([,.;:)])/g, '$1')
      .replace(/\(\s+/g, '(')
      .replace(/\s{2,}/g, ' ')
      .replace(SEPARATORY, '')
      .trim()
    if (t === przed) break
  }
  return t.length >= 3 ? t : pelna
}

/**
 * Wiersz listy modeli albo podpowiedzi, jednakowy dla trzech kategorii. Przyjmuje wiersz
 * `*_models/` (klucze angielskie) albo wynik `szukaj_modeli/` (klucze polskie).
 * `moc`: W (moduły), kW (falowniki), kWh (magazyny). `dane`: wiersz z API do mapowania.
 */
export const pozycjaListy = (kategoria, s = {}) => {
  const nazwa = tekst(pierwsza(s.name, s.nazwa)) || ''
  const producent = tekst(pierwsza(s.manufacturer, s.producent))
  const moc = dodatnia(kategoria === 'magazyn'
    ? pierwsza(s.capacity, s.pojemnosc_kwh)
    : pierwsza(s.power, s.moc_w, s.moc_kw, s.moc))
  const ofert = liczba(pierwsza(s.offer_count, s.ofert)) ?? 0
  let szczegoly = []
  if (kategoria === 'moduly') {
    const typy = typyZDanych(s)
    const czarnaRama = /czarn/i.test(s.frame_colour || '') && !typy.some((t) => /full\s*black/i.test(t))
    szczegoly = [typy.join(', '), czarnaRama && 'czarna rama', dodatnia(s.length_variant) && `${s.length_variant} mm`]
  } else if (kategoria === 'falownik') {
    const fazy = fazyZOpisu(pierwsza(s.fazy, s.phase))
    szczegoly = [tekst(pierwsza(s.type, s.typ, s.rodzaj)), fazy && fazyTekst(fazy)]
  } else {
    szczegoly = [tekst(pierwsza(s.type, s.typ)), tekst(pierwsza(s.voltage, s.napiecie))]
  }
  return {
    id: pierwsza(s.id, s.model_id),
    nazwa,
    krotka: krotkaNazwa(nazwa, producent),
    producent,
    seria: tekst(pierwsza(s.series, s.seria)),
    moc,
    ofert,
    img: adres(s.img_url),
    opis: [...szczegoly, ofert > 0 && liczbaOfert(ofert)].filter(Boolean).join(' · '),
    dane: s,
  }
}

/** Serie z listy pozycji: [{ value, count }], od najliczniejszej. */
export const opcjeSerii = (pozycje) => {
  const licznik = new Map()
  pozycje.forEach((p) => { if (p.seria) licznik.set(p.seria, (licznik.get(p.seria) || 0) + 1) })
  return [...licznik.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'pl'))
}

/** Moce (pojemności) z listy pozycji, rosnąco, bez powtórzeń. */
export const opcjeMocy = (pozycje) => [...new Set(pozycje.map((p) => p.moc).filter((m) => m != null))].sort((a, b) => a - b)

/** Pozycje danej serii i mocy; pusty filtr ('' albo null) przepuszcza wszystko. */
export const filtrujPozycje = (pozycje, { seria = '', moc = '' } = {}) => {
  const m = moc === '' || moc == null ? null : liczba(moc)
  return pozycje.filter((p) => (!seria || p.seria === seria) && (m == null || (p.moc != null && Math.abs(p.moc - m) < 1e-6)))
}

/** Od najmniejszej mocy; przy tej samej mocy najpierw częściej oferowane. Bez mocy na końcu. */
export const sortujPozycje = (pozycje) => [...pozycje].sort((a, b) => {
  if (a.moc == null || b.moc == null) return (a.moc == null) - (b.moc == null)
  return a.moc - b.moc || b.ofert - a.ofert || a.krotka.localeCompare(b.krotka, 'pl')
})
